import React, { useState, useEffect } from 'react';

interface CatalogData {
  objects: any[];
  related_objects: any[];
}

interface CartItem {
  id: string;
  variationId?: string;
  name: string;
  price: number;
  imageUrl?: string;
  quantity: number;
  modifiers?: {
    id: string;
    name: string;
    price: number;
    type?: 'CHOICE';
  }[];
}

function Shop() {
  const [catalog, setCatalog] = useState<CatalogData>({ objects: [], related_objects: [] });
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedModifierId, setSelectedModifierId] = useState<string>('');
  const [giftMessage, setGiftMessage] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState<'DELIVERY' | 'PICKUP' | ''>('');
  const [selectedVariationId, setSelectedVariationId] = useState<string>('');

  // Sender (buyer) details
  const [senderName, setSenderName] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [senderEmail, setSenderEmail] = useState('');

  // Recipient details
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [recipientSuburb, setRecipientSuburb] = useState('');
  const [recipientPostcode, setRecipientPostcode] = useState('');

  // Explicit references so ESLint sees the setters as used (they are also used in JSX below)
  void setSenderName;
  void setSenderPhone;
  void setSenderEmail;
  void setRecipientAddress;
  void setRecipientSuburb;
  void setRecipientPostcode;

  // Fetch catalog
  useEffect(() => {
    fetch('/api/getCatalog')
      .then(res => res.json())
      .then(data => {
        setCatalog({
          objects: data.objects || [],
          related_objects: data.related_objects || []
        });
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load catalog:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedProduct(null);
        setSelectedModifierId('');
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  if (loading) {
    return (
      <div className="py-32 text-center text-2xl font-light text-gray-500">
        Loading beautiful flowers...
      </div>
    );
  }

  const items = catalog.objects.filter(obj => obj.type === 'ITEM') || [];
  const images = catalog.objects.filter(obj => obj.type === 'IMAGE') || [];
  const categories = [
    ...catalog.objects.filter(obj => obj.type === 'CATEGORY'),
    ...catalog.related_objects.filter(obj => obj.type === 'CATEGORY')
  ];

  const allCatalogObjects = [
    ...(catalog.objects || []),
    ...(catalog.related_objects || [])
  ];

  const modifierLists = allCatalogObjects.filter(obj => obj.type === 'MODIFIER_LIST');

  const getImageUrl = (imageIds?: string[]) => {
    if (!imageIds || imageIds.length === 0) return null;
    const image = images.find(img => img.id === imageIds[0]);
    return image?.imageData?.url || null;
  };

  const filteredItems = selectedCategory === 'all'
    ? items
    : items.filter(item => {
      const itemData = item.itemData || {};
      return (
        itemData.categoryId === selectedCategory ||
        (Array.isArray(itemData.categoryIds) && itemData.categoryIds.includes(selectedCategory)) ||
        (Array.isArray(itemData.categories) && itemData.categories.some((c: any) => c.id === selectedCategory))
      );
    });

  const addToCart = (
    item: any,
    selectedModifier: any = null,
    variationId?: string
  ) => {
    const itemData = item.itemData || {};
    const variations = itemData.variations || [];
    const selectedVariation =
      variations.find((v: any) => v.id === variationId) || variations[0];

    const basePrice = selectedVariation?.itemVariationData?.priceMoney?.amount
      ? Number(selectedVariation.itemVariationData.priceMoney.amount) / 100
      : 0;

    const variationName = selectedVariation?.itemVariationData?.name || '';

    const modifierPrice = selectedModifier
      ? Number(selectedModifier.modifierData?.priceMoney?.amount || 0) / 100
      : 0;

    const totalPrice = basePrice + modifierPrice;
    const imageUrl = getImageUrl(itemData.imageIds) || undefined;

    const modifiers: CartItem['modifiers'] = [];

    if (selectedModifier) {
      modifiers.push({
        id: selectedModifier.id,
        name: selectedModifier.modifierData?.name || 'Add-on',
        price: modifierPrice,
        type: 'CHOICE'
      });
    }

    const displayName =
      variationName && variationName !== 'Regular'
        ? `${itemData.name} (${variationName})`
        : itemData.name || 'Beautiful Arrangement';

    setCart(prev => {
      const existing = prev.find(i =>
        i.id === item.id &&
        i.variationId === selectedVariation?.id &&
        JSON.stringify(i.modifiers) === JSON.stringify(modifiers)
      );

      if (existing) {
        return prev.map(i =>
          i.id === existing.id &&
          i.variationId === existing.variationId &&
          JSON.stringify(i.modifiers) === JSON.stringify(modifiers)
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }

      return [
        ...prev,
        {
          id: item.id,
          variationId: selectedVariation?.id,
          name: displayName,
          price: totalPrice,
          imageUrl,
          quantity: 1,
          modifiers
        }
      ];
    });
  };

  const updateQuantity = (
    id: string,
    change: number,
    modifiers: CartItem['modifiers'] = [],
    variationId?: string
  ) => {
    setCart(prev =>
      prev
        .map(item => {
          const sameModifiers =
            JSON.stringify(item.modifiers || []) === JSON.stringify(modifiers || []);
          const sameVariation = (item.variationId || '') === (variationId || '');

          if (item.id === id && sameVariation && sameModifiers) {
            const newQty = item.quantity + change;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean) as CartItem[]
    );
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const getProductChoiceModifiers = (product: any) => {
    const listIds = (product?.itemData?.modifierListInfo || [])
      .filter((info: any) => info.enabled !== false)
      .map((info: any) => info.modifierListId);

    return modifierLists
      .filter(list => listIds.includes(list.id))
      .filter(list => list.modifierListData?.modifierType !== 'TEXT')
      .flatMap(list => list.modifierListData?.modifiers || [])
      .filter((mod: any) => !mod.isDeleted);
  };

  const handleCheckout = async () => {
    if (!fulfillmentType) {
      alert('Please choose Delivery or Pickup');
      return;
    }

    // Sender always required
    if (!senderName.trim()) {
      alert('Please enter your name');
      return;
    }
    if (!senderPhone.trim()) {
      alert('Please enter your phone number');
      return;
    }
    if (!senderEmail.trim() || !senderEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    // Recipient required for delivery
    if (fulfillmentType === 'DELIVERY') {
      if (!recipientName.trim()) {
        alert('Please enter the recipient\'s name');
        return;
      }
      if (!recipientPhone.trim()) {
        alert('Please enter the recipient\'s phone number');
        return;
      }
      if (!recipientAddress.trim()) {
        alert('Please enter the delivery address');
        return;
      }
      if (!recipientSuburb.trim()) {
        alert('Please enter the suburb');
        return;
      }
      if (!recipientPostcode.trim()) {
        alert('Please enter the postcode');
        return;
      }
    }

    setIsCheckingOut(true);
    try {
      const response = await fetch('/api/createCheckout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cart,
          giftMessage: giftMessage.trim() || null,
          fulfillmentType,
          origin: window.location.origin,
          senderName: senderName.trim(),
          senderPhone: senderPhone.trim(),
          senderEmail: senderEmail.trim(),
          recipientName: recipientName.trim() || null,
          recipientPhone: recipientPhone.trim() || null,
          recipientAddress: recipientAddress.trim() || null,
          recipientSuburb: recipientSuburb.trim() || null,
          recipientPostcode: recipientPostcode.trim() || null
        })
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        const msg = data.error || data.detail || JSON.stringify(data);
        alert('Checkout failed: ' + msg);
        setIsCheckingOut(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setIsCheckingOut(false);
    }
  };

  const openProduct = (item: any) => {
    setSelectedProduct(item);
    setCurrentImageIndex(0);
    setSelectedModifierId('');
    const firstVar = item.itemData?.variations?.[0];
    setSelectedVariationId(firstVar?.id || '');
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 relative">

      {/* Floating Cart Button */}
      <button
        onClick={() => setIsCartOpen(true)}
        className="fixed bottom-8 right-8 bg-primary hover:bg-primary-dark text-white px-6 py-4 rounded-full shadow-2xl transition z-40 flex items-center gap-3 font-medium text-lg"
      >
        <span>🛒</span>
        <span>View Cart</span>
        {cartItemCount > 0 && (
          <span className="bg-white text-primary rounded-full px-2.5 py-0.5 text-sm font-bold">
            {cartItemCount}
          </span>
        )}
      </button>

      {/* Header */}
      <h1
        className="text-5xl font-serif text-center mb-4"
        style={{ fontFamily: "'Pinyon Script', cursive", color: '#599FBC' }}
      >
        Our Collection
      </h1>
      <p className="text-center text-gray-600 mb-12">
        Fresh, seasonal & delivered on Friday or Saturday
        <br />
        Order before 4pm Thursday for guaranteed delivery.
      </p>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-3 justify-center mb-12">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-6 py-2 rounded-full text-sm font-medium transition ${selectedCategory === 'all' ? 'bg-primary text-white' : 'bg-white hover:bg-gray-100'
            }`}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition ${selectedCategory === cat.id ? 'bg-primary text-white' : 'bg-white hover:bg-gray-100'
              }`}
          >
            {cat.categoryData?.name || 'Category'}
          </button>
        ))}
      </div>

      {/* Product Grid */}
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8">
        {filteredItems.map(item => {
          const itemData = item.itemData || {};
          const imageUrl = getImageUrl(itemData.imageIds);
          const priceAmount = itemData.variations?.[0]?.itemVariationData?.priceMoney?.amount;
          const price = priceAmount ? (Number(priceAmount) / 100).toFixed(2) : '0.00';

          return (
            <div
              key={item.id}
              onClick={() => openProduct(item)}
              className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition group cursor-pointer flex flex-col justify-between"
            >
              <div>
                <div className="h-72 bg-gray-100 flex items-center justify-center overflow-hidden">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={itemData.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                    />
                  ) : (
                    <span className="text-8xl">🌸</span>
                  )}
                </div>
                <div className="p-6 pb-0">
                  <h3 className="text-xl font-medium mb-2">{itemData.name}</h3>
                  <p className="text-3xl font-light text-primary">From ${price}</p>
                </div>
              </div>

              <div className="p-6 pt-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openProduct(item);
                  }}
                  className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-2xl transition text-sm font-medium"
                >
                  View
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setSelectedProduct(null);
            setSelectedModifierId('');
          }}
        >
          <div
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => {
                  setSelectedProduct(null);
                  setSelectedModifierId('');
                }}
                className="absolute top-4 right-4 z-20 bg-white/90 hover:bg-white text-gray-700 hover:text-gray-900 w-10 h-10 rounded-full flex items-center justify-center text-2xl shadow-md transition"
              >
                ✕
              </button>

              {/* Image Slider */}
              {(() => {
                const itemData = selectedProduct.itemData || {};
                const imageIds = itemData.imageIds || [];
                const currentImageUrl = getImageUrl([imageIds[currentImageIndex]]);

                const nextImage = (e: React.MouseEvent) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev + 1) % imageIds.length);
                };

                const prevImage = (e: React.MouseEvent) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev - 1 + imageIds.length) % imageIds.length);
                };

                return (
                  <div className="relative">
                    {currentImageUrl ? (
                      <img
                        src={currentImageUrl}
                        alt={itemData.name}
                        className="w-full h-96 object-cover rounded-t-3xl"
                      />
                    ) : (
                      <div className="h-96 bg-gray-100 flex items-center justify-center text-9xl rounded-t-3xl">
                        🌸
                      </div>
                    )}

                    {imageIds.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 w-10 h-10 rounded-full flex items-center justify-center shadow"
                        >
                          ‹
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 w-10 h-10 rounded-full flex items-center justify-center shadow"
                        >
                          ›
                        </button>

                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {imageIds.map((_: string, idx: number) => (
                            <button
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentImageIndex(idx);
                              }}
                              className={`w-2.5 h-2.5 rounded-full transition ${
                                idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              <div className="p-8">
                <h2 className="text-4xl font-serif mb-4">
                  {selectedProduct.itemData?.name}
                </h2>

                {selectedProduct.itemData?.description && (
                  <p className="text-gray-700 leading-relaxed mb-6 whitespace-pre-line">
                    {selectedProduct.itemData.description}
                  </p>
                )}

                {/* Size / Variation selector */}
                {(() => {
                  const variations = selectedProduct.itemData?.variations || [];
                  const selectedVariation = variations.find((v: any) => v.id === selectedVariationId) || variations[0];
                  const selectedPrice = selectedVariation?.itemVariationData?.priceMoney?.amount
                    ? Number(selectedVariation.itemVariationData.priceMoney.amount) / 100
                    : 0;

                  return (
                    <>
                      {variations.length > 1 && (
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Choose size
                          </label>
                          <select
                            value={selectedVariationId}
                            onChange={(e) => setSelectedVariationId(e.target.value)}
                            className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            {variations.map((v: any) => (
                              <option key={v.id} value={v.id}>
                                {v.itemVariationData?.name} — $
                                {(Number(v.itemVariationData?.priceMoney?.amount || 0) / 100).toFixed(2)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <p className="text-3xl font-light text-primary mb-6">
                        From ${selectedPrice.toFixed(2)}
                      </p>
                    </>
                  );
                })()}

                {/* Add-ons (choice modifiers) */}
                {(() => {
                  const productModifiers = getProductChoiceModifiers(selectedProduct);
                  if (productModifiers.length === 0) return null;

                  return (
                    <div className="mb-6">
                      <p className="block text-sm font-medium text-gray-700 mb-3">
                        Add-ons (optional)
                      </p>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="addon"
                            value=""
                            checked={selectedModifierId === ''}
                            onChange={() => setSelectedModifierId('')}
                            className="w-4 h-4 accent-primary"
                          />
                          <span>No add-on</span>
                        </label>

                        {productModifiers.map((mod: any) => {
                          const price = Number(mod.modifierData?.priceMoney?.amount || 0) / 100;
                          return (
                            <label key={mod.id} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="radio"
                                name="addon"
                                value={mod.id}
                                checked={selectedModifierId === mod.id}
                                onChange={() => setSelectedModifierId(mod.id)}
                                className="w-4 h-4 accent-primary"
                              />
                              <span>
                                {mod.modifierData?.name} (+ ${price.toFixed(2)})
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <button
                  onClick={() => {
                    const productModifiers = getProductChoiceModifiers(selectedProduct);
                    const selectedModifier = productModifiers.find((mod: any) => mod.id === selectedModifierId) || null;

                    addToCart(selectedProduct, selectedModifier, selectedVariationId);

                    setSelectedProduct(null);
                    setSelectedModifierId('');
                  }}
                  className="w-full bg-primary hover:bg-primary-dark text-white py-4 rounded-2xl text-lg font-medium transition"
                >
                  Add to Cart
                </button>

                {/* Custom / Upgrade message */}
                <div className="mt-8 pt-6 border-t text-center">
                  <p className="text-gray-600 mb-4">
                    Looking for something special and unique?<br />
                    Contact us to see what we can do.
                  </p>
                  <a
                    href="/contact"
                    className="inline-block border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 rounded-full transition font-medium"
                  >
                    Enquire Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      {isCartOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
          <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
            {/* Fixed header */}
            <div className="flex justify-between items-center p-6 pb-4 border-b flex-shrink-0">
              <h2 className="text-2xl font-serif">Your Cart</h2>
              <button
                onClick={() => setIsCartOpen(false)}
                className="text-2xl text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Scrollable middle: items + sender + recipient + gift + fulfillment */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cart.length === 0 ? (
                <p className="text-gray-500 text-center py-12">
                  Your shopping cart is empty 🌸
                </p>
              ) : (
                <div className="space-y-4 mb-6">
                  {cart.map(item => (
                    <div
                      key={`${item.id}-${item.variationId || 'default'}-${JSON.stringify(item.modifiers)}`}
                      className="flex gap-4 bg-gray-50 p-3 rounded-2xl items-center"
                    >
                      <div className="w-16 h-16 bg-gray-200 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl">🌸</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 line-clamp-1">
                          {item.name}
                        </h4>

                        {item.modifiers && item.modifiers.length > 0 && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            {item.modifiers.map(mod => `+ ${mod.name}`).join(', ')}
                          </p>
                        )}

                        <p className="text-primary font-light">
                          ${item.price.toFixed(2)}
                        </p>

                        <div className="flex items-center gap-2 mt-1">
                          <button
                            onClick={() => updateQuantity(item.id, -1, item.modifiers, item.variationId)}
                            className="bg-white border w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold hover:bg-gray-100"
                          >
                            −
                          </button>
                          <span className="text-sm font-medium w-4 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, 1, item.modifiers, item.variationId)}
                            className="bg-white border w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold hover:bg-gray-100"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Sender (buyer) details */}
              <div className="mb-6 border-t pt-6">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Your details <span className="text-red-500">*</span>
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Your name</label>
                    <input
                      type="text"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="Your full name"
                      className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Your phone</label>
                    <input
                      type="tel"
                      value={senderPhone}
                      onChange={(e) => setSenderPhone(e.target.value)}
                      placeholder="Your mobile number"
                      className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Your email</label>
                    <input
                      type="email"
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Recipient details */}
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Who is this order for? {fulfillmentType === 'DELIVERY' && <span className="text-red-500">*</span>}
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Recipient name {fulfillmentType === 'DELIVERY' && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Name of the person receiving the flowers"
                      className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Recipient phone {fulfillmentType === 'DELIVERY' && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="tel"
                      value={recipientPhone}
                      onChange={(e) => setRecipientPhone(e.target.value)}
                      placeholder="Phone number for the driver"
                      className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  {fulfillmentType === 'DELIVERY' && (
                    <>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Delivery address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={recipientAddress}
                          onChange={(e) => setRecipientAddress(e.target.value)}
                          placeholder="Street address"
                          className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Suburb <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={recipientSuburb}
                            onChange={(e) => setRecipientSuburb(e.target.value)}
                            placeholder="Orange"
                            className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Postcode <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={recipientPostcode}
                            onChange={(e) => setRecipientPostcode(e.target.value)}
                            placeholder="2800"
                            className="w-full p-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Gift Message */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gift message (optional)
                </label>
                <textarea
                  value={giftMessage}
                  onChange={(e) => setGiftMessage(e.target.value)}
                  placeholder="Write a short message for the recipient..."
                  rows={3}
                  className="w-full p-3 border rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Delivery or Pickup */}
              <div className="mb-6">
                <p className="block text-sm font-medium text-gray-700 mb-3">
                  How would you like to receive your order? <span className="text-red-500">*</span>
                </p>
                <div className="flex flex-col gap-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="fulfillment"
                      value="DELIVERY"
                      checked={fulfillmentType === 'DELIVERY'}
                      onChange={() => setFulfillmentType('DELIVERY')}
                      className="w-4 h-4 accent-primary"
                    />
                    <span>Delivery</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="fulfillment"
                      value="PICKUP"
                      checked={fulfillmentType === 'PICKUP'}
                      onChange={() => setFulfillmentType('PICKUP')}
                      className="w-4 h-4 accent-primary"
                    />
                    <span>Pickup</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Sticky footer: total + button */}
            <div className="border-t p-6 flex-shrink-0 bg-white">
              <div className="flex justify-between items-center text-xl mb-4">
                <span className="font-light text-gray-600">Total:</span>
                <span className="font-serif font-bold text-gray-900">
                  ${cartTotal.toFixed(2)}
                </span>
              </div>
              <button
                disabled={cart.length === 0 || isCheckingOut}
                onClick={handleCheckout}
                className="w-full bg-primary hover:bg-primary-dark disabled:bg-gray-300 text-white py-4 rounded-2xl text-center font-medium transition shadow-md"
              >
                {isCheckingOut ? 'Loading Secure Checkout...' : 'Proceed to Checkout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Shop;
